import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ManipulationCodes } from '@common/knownTypes/ManipulationCodes';
import { ModalService } from '@common/services/modal.service';
import { ServiceRequestService } from '@common/services/service-request.service';
import { ShipmentPoolService } from '@common/services/shipment-pool.service';
import { maxLengthValidator } from '@common/validators/max-length-validator';
import { requiredValidator } from '@common/validators/required-validator';
import { DialogRef } from '@progress/kendo-angular-dialog';
import { ToastService } from '@services/toast.service';
import { catchError, first, map, Observable, of, Subject, takeUntil, tap, throwError } from 'rxjs';

@Component({
    selector: 'app-create-service-request-from-cargo-manifest-modal',
    templateUrl: './create-service-request-from-cargo-manifest-modal.component.html',
    styleUrls: ['./create-service-request-from-cargo-manifest-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CreateServiceRequestFromCargoManifestModalComponent implements OnInit, AfterViewInit, OnDestroy {
    public containers: boolean;
    public roro: boolean;
    public systemId: string;
    public vesselVisitId: number;
    public terminalId: string;
    public terminalOperatorId: number;
    public portId: string;
    isBusy: boolean;
    form: UntypedFormGroup;
    isEditMode: boolean;
    mrnSelect: boolean = true;

    manipulations$: Observable<any>;
    mrn$: Observable<any>;
    mrnItem$: Observable<any>;
    secondaryTerminals$: Observable<any>;
    customsWarehouses$: Observable<any>;
    loadingVesselVisits$: Observable<any>;
    loadingVessels$: Observable<any>;
    destroyed$ = new Subject<void>();

    readonly manipulationCodes = ManipulationCodes;

    selectLabel = (item) => {
        if (item.id) return `${item.name}`;
        if (item.localizedCode) return `${item.localizedCode} - ${item.name}`;
        if (item.code) return `${item.code} - ${item.name}`;
    };

    constructor(
        public modalService: ModalService,
        public activeModal: DialogRef,
        private cdr: ChangeDetectorRef,
        private serviceRequestService: ServiceRequestService,
        private shipmentPoolService: ShipmentPoolService,
        private toastyService: ToastService
    ) {}

    ngOnInit() {
        this.form = this.createFormGroup();
        this.initializeDefaultValues();
    }

    ngAfterViewInit() {
        this.trackFormChanges();
        this.cdr.detectChanges();
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    toggleMRNSelect(event: any) {
        event.preventDefault();
        this.mrnSelect = !this.mrnSelect;
        if (!this.mrnSelect) {
            this.form.get('mrnId').setValue(null);
        } else {
            this.form.get('mrnString').patchValue(null);
        }
    }

    searchMRN(event: any) {
        event.preventDefault();
        const mrnString = this.form.get('mrnString').value;
        const mrnType = this.form.get('customsDeclarationCodeId').value;
        if (mrnString && mrnString.length > 0) {
            this.shipmentPoolService
                .getMrnByMrnString({
                    mrnString,
                    mrnType,
                    portId: this.portId
                })
                .pipe(
                    first(),
                    catchError((err) => {
                        return throwError(err);
                    })
                )
                .subscribe((data) => {
                    if (data) {
                        this.toastyService.info('MRN found');
                        this.form.get('mrnId').setValue(data.id);
                    } else {
                        this.toastyService.info('MRN not found');
                    }
                });
        }
    }

    get isBulkManifest(): boolean {
        return !this.containers && !this.roro;
    }

    get showVsVsLoadingFields(): boolean {
        return this.isBulkManifest && this.form?.get('manipulationId')?.value === ManipulationCodes.VSVS;
    }

    private initializeDefaultValues() {
        this.manipulations$ = this.serviceRequestService
            .getManipulationsOnServiceRequest({
                tosSystem: this.systemId,
                forCargoManifest: true
            })
            .pipe(
                map((manipulations) =>
                    this.isBulkManifest
                        ? manipulations
                        : manipulations?.filter((manipulation) => manipulation?.id !== ManipulationCodes.VSVS)
                )
            );

        this.mrn$ = this.shipmentPoolService.getMrnsForCurrentUser({ portId: this.portId }).pipe(
            first(),
            tap((mrns: any[]) => {
                if (Array.isArray(mrns)) {
                    const matchingVesselVisitIds = mrns.filter((m) => m.vesselVisitId === this.vesselVisitId);
                    if (matchingVesselVisitIds.length > 0) {
                        this.form.get('mrnId').patchValue(matchingVesselVisitIds[0].id);
                    }
                }
            })
        );

        this.secondaryTerminals$ = this.serviceRequestService.getSecondaryTerminalsOnServiceRequest(
            this.terminalOperatorId,
            this.terminalId,
            this.systemId,
            this.portId,
            false
        );

        this.customsWarehouses$ = this.serviceRequestService.getCustomsWarehouses({
            organizationId: this.terminalOperatorId
        });

        this.loadingVesselVisits$ = this.serviceRequestService.getLoadingVesselVisits({ portId: this.portId });
    }

    private trackFormChanges() {
        this.form
            .get('mrnId')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe((val) => {
                if (val) {
                    this.mrnItem$ = this.shipmentPoolService.getCustomsDeclarationItemsForMrn(val);
                    this.form.get('mrnItemId').enable();

                    this.serviceRequestService
                        .getCustomsWarehouseForMrn(val, this.terminalOperatorId)
                        .subscribe((data: any) => {
                            if (!data?.warehouse) return;
                            this.form.get('customsWarehouseId').patchValue(data.warehouse);
                            this.form.get('customsWarehouseId').disable();
                        });
                } else {
                    this.form.get('mrnItemId').patchValue(null);
                    this.form.get('mrnItemId').disable();

                    this.form.get('customsWarehouseId').enable();
                }
            });

        this.form
            .get('manipulationId')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.updateVsVsValidators();
            });

        this.form
            .get('loadingVesselVoyage')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe((value) => {
                this.onLoadingVesselVoyageChange(value);
            });

        this.form
            .get('loadingVesselId')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.updateVsVsValidators();
            });
    }

    private createFormGroup() {
        const form = new UntypedFormGroup({
            manipulationId: new UntypedFormControl({ value: 'VSIN', disabled: this.containers }, requiredValidator()),
            cargoUnitSpecification: new UntypedFormControl({ value: false, disabled: !this.isBulkManifest }),
            motNbr: new UntypedFormControl(null),
            mrnId: new UntypedFormControl(null),
            customsDeclarationCodeId: new UntypedFormControl(null),
            mrnString: new UntypedFormControl(null),
            mrnItemId: new UntypedFormControl({ value: null, disabled: true }),
            lrn: new UntypedFormControl(null, [maxLengthValidator(22)]),
            customsWarehouseId: new UntypedFormControl(null),
            requiredStartDate: new UntypedFormControl(null),
            contractNbr: new UntypedFormControl(null),
            secondaryTerminalId: new UntypedFormControl(null),
            remark: new UntypedFormControl(null),
            loadingVesselVoyage: new UntypedFormControl(null),
            loadingVesselId: new UntypedFormControl(null),
            loadingVoyageNumber: new UntypedFormControl(null)
        });
        form.patchValue(this);
        return form;
    }

    private updateVsVsValidators() {
        const loadingVesselVoyageControl = this.form.get('loadingVesselVoyage');

        if (this.showVsVsLoadingFields) {
            loadingVesselVoyageControl.setValidators(requiredValidator());
        } else {
            loadingVesselVoyageControl.clearValidators();
            loadingVesselVoyageControl.patchValue(null, { emitEvent: false });
            this.form.get('loadingVesselId').patchValue(null, { emitEvent: false });
            this.form.get('loadingVoyageNumber').patchValue(null, { emitEvent: false });
            this.loadingVessels$ = of([]);
        }

        loadingVesselVoyageControl.updateValueAndValidity({ emitEvent: false });
    }

    private onLoadingVesselVoyageChange(value: number) {
        const loadingVesselIdControl = this.form.get('loadingVesselId');
        const loadingVoyageNumberControl = this.form.get('loadingVoyageNumber');

        if (!this.showVsVsLoadingFields) {
            return;
        }

        if (!value) {
            loadingVesselIdControl.patchValue(null, { emitEvent: false });
            loadingVoyageNumberControl.patchValue(null, { emitEvent: false });
            loadingVoyageNumberControl.enable({ emitEvent: false });
            this.loadingVessels$ = of([]);
            return;
        }

        this.serviceRequestService
            .getVesselFromVesselVisit(value, this.form.get('manipulationId').value)
            .subscribe((data) => {
                this.loadingVessels$ = of(data?.vessels ?? []);
                if (data?.vessels?.length === 1) {
                    loadingVesselIdControl.patchValue(data.vessels[0]?.id, { emitEvent: false });
                }
                loadingVoyageNumberControl.patchValue(data?.voyageNumber, { emitEvent: false });
                loadingVoyageNumberControl.disable({ emitEvent: false });
            });
    }

    submit() {
        this.activeModal.close(this.form.getRawValue());
    }
}
